import { observer } from 'mobx-react';
import { useAuthStore } from '../../../contexts/mobx';
import CTLogo from '../../../icons/ct_logo_rounded.svg?react';
import { usePreview } from '../context/PreviewContext';
import { useT } from '../context/useT';
import { usePreviewTheme } from '../theme/ThemeProvider';
import style from './style.module.scss';

/**
 * Bag/gift glyph used inside the "My Clubs" pill. Inlined so we don't
 * have to ship a tiny one-off SVG asset for it; can be replaced with a
 * real icon set when one is adopted.
 */
const GiftIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path d="M14 5h-2.18A3 3 0 0 0 8 1.5 3 3 0 0 0 4.18 5H2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h.5v5a1.5 1.5 0 0 0 1.5 1.5h8a1.5 1.5 0 0 0 1.5-1.5V9h.5a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1Zm-4 9H4v-5h3.5v5Zm0-7H2.5V6.5H7.5V7Zm-3-3a1 1 0 1 1 2 0v.5h-2V4Zm3 .5V4a1 1 0 1 1 2 0v.5h-2Zm5 9.5h-3.5v-5H12v5Zm0-7H8.5V6.5h5V7Z" />
    </svg>
);

/**
 * Renders the app logo for both BUs. The CTCAN-117 revamp does not
 * include a dedicated CK Figma, so the Couche-Tard glyph is reused for
 * Circle K too; `theme.logo.id` is kept around purely as an aria-label
 * hint and as an extension point if a CK asset is ever added.
 *
 * The Figma calls for the red glyph inside a white circle. The asset
 * itself is just the glyph, so we wrap it in a styled `<span>` that
 * provides the white pill background.
 */
const PreviewBrandLogo = () => {
    const theme = usePreviewTheme();
    const ariaLabel = theme.logo.id === 'CT' ? 'Couche-Tard' : 'Circle K';
    return (
        <span className={style.logo} role="img" aria-label={ariaLabel}>
            <CTLogo className={style.logo_glyph} fill="currentColor" />
        </span>
    );
};

/**
 * Header rendered at the top of the simulated home page. Reproduces the
 * CTCAN-117 Figma: BU logo, "Hi, {name}!" greeting that swaps between
 * morning and afternoon variants, and a pill-shaped "My Clubs" CTA that
 * leads to the beverage clubs section.
 *
 * The header background follows `--preview-primary` so it automatically
 * recolors when the user switches BU in the Customize panel. The
 * "My Clubs" button is intentionally inert for now — wiring it to the
 * beverage clubs screen is tracked as a follow-up of CTCAN-117.
 */
export const PreviewHeader = observer(() => {
    const t = useT();
    const { language } = usePreview();
    const authStore = useAuthStore();
    const { claims } = authStore;

    const firstName = claims?.name
        ? claims.name.substring(0, claims.name.indexOf(' '))
        : '';

    return (
        <header className={style.header} lang={language}>
            <div className={style.identity_row}>
                <PreviewBrandLogo />
                <span className={style.greeting}>
                    {t.home.greeting} {firstName}!
                </span>
            </div>
            <button type="button" className={style.my_clubs_button}>
                <GiftIcon />
                <span>{t.home.myClubs}</span>
            </button>
        </header>
    );
});
